import type { LocalTime } from "@js-joda/core";
import type { FC } from "react";
import type { MyBusinessTimeOfDay } from "shared";
import type { UseFormReturn } from "../common/form/useForm";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Button } from "../components/Button";
import type { FormMyBusinessLocationFields } from "./FormBusinessLocationFields";
import styles from "./FormBusinessRegularHours.module.css";
import { LocalTimeToGoogleTime, timeOfDayToLocalTime } from "./helpers";
import { TimeRange } from "./TimeRange";

type Props = {
	form: UseFormReturn<FormMyBusinessLocationFields>;
};

type SpecialHourRow = NonNullable<
	FormMyBusinessLocationFields["specialHours"]
>[number];

function dateToInput(period: SpecialHourRow, key: "startDate" | "endDate") {
	return period[key] ?? "";
}

export const FormBusinessSpecialHours: FC<Props> = ({ form }) => {
	const periods = form.values.specialHours ?? [];

	const setPeriods = (next: SpecialHourRow[]) => {
		form.setFieldValue("specialHours", next);
	};

	const handleTimeChange = (
		index: number,
		value: [LocalTime | null, LocalTime | null],
	) => {
		const googleTime: [
			MyBusinessTimeOfDay | undefined,
			MyBusinessTimeOfDay | undefined,
		] = [
			value[0] ? LocalTimeToGoogleTime(value[0]) : undefined,
			value[1] ? LocalTimeToGoogleTime(value[1]) : undefined,
		];
		const next = [...periods];
		next[index] = {
			...next[index],
			openTime: googleTime[0],
			closeTime: googleTime[1],
			closed: false,
		};
		setPeriods(next);
	};

	return (
		<div className={styles.list}>
			{periods.length === 0 && (
				<p className="text-muted mb-2" style={{ fontSize: "0.875rem" }}>
					Add holiday or special hours so listings stay accurate on closed days.
				</p>
			)}
			{periods.map((period, index) => (
				<div
					key={period.id ?? `special-${period.startDate}-${period.endDate}`}
					className={styles.day}
					style={{ flexWrap: "wrap", gap: "0.5rem" }}
				>
					<ExtraForm.Group label="Start" className="mb-0">
						<input
							type="date"
							className="form-control form-control-sm"
							value={dateToInput(period, "startDate")}
							onChange={(e) => {
								const next = [...periods];
								next[index] = { ...next[index], startDate: e.target.value };
								setPeriods(next);
							}}
						/>
					</ExtraForm.Group>
					<ExtraForm.Group label="End" className="mb-0">
						<input
							type="date"
							className="form-control form-control-sm"
							value={dateToInput(period, "endDate")}
							onChange={(e) => {
								const next = [...periods];
								next[index] = { ...next[index], endDate: e.target.value };
								setPeriods(next);
							}}
						/>
					</ExtraForm.Group>
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 4,
							fontSize: "0.875rem",
						}}
					>
						<input
							type="checkbox"
							checked={!!period.closed}
							onChange={(e) => {
								const next = [...periods];
								next[index] = {
									...next[index],
									closed: e.target.checked,
									openTime: e.target.checked ? undefined : next[index].openTime,
									closeTime: e.target.checked
										? undefined
										: next[index].closeTime,
								};
								setPeriods(next);
							}}
						/>
						Closed
					</label>
					{!period.closed && (
						<TimeRange
							value={[
								timeOfDayToLocalTime(period.openTime),
								timeOfDayToLocalTime(period.closeTime),
							]}
							onChange={(value) => handleTimeChange(index, value)}
						/>
					)}
					<Button
						variant="ghost"
						size="sm"
						icon="delete"
						aria-label="Remove holiday hours"
						onClick={() => setPeriods(periods.filter((_, i) => i !== index))}
					/>
				</div>
			))}
			<Button
				variant="ghost"
				size="sm"
				icon="add"
				onClick={() =>
					setPeriods([
						...periods,
						{
							id: `new-${Date.now()}`,
							startDate: "",
							endDate: "",
							closed: true,
						},
					])
				}
			>
				Add holiday hours
			</Button>
		</div>
	);
};
